import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';
import { getSupervisionSearchStr } from '../../utils/common';

import type { IBtSupervision } from '../../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'bt_supervisions';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<BtSupervisionState>) =>
    createExtraReducers(builder),
});

export interface BtSupervisionState {
  list: Array<IBtSupervision>;
  error: any;
  loading: boolean;
  current: IBtSupervision | null;
}

function createInitialState(): BtSupervisionState {
  return {
    list: [],
    loading: false,
    error: null,
    current: null,
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
  };

  function clearState(state: BtSupervisionState) {
    state.list = [];
    state.current = null;
    state.error = null;
    state.loading = false;
  }

  function updateState(state: BtSupervisionState, action: any) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/bt_supervisions`;

  return {
    getList: getList(),
    startBtSupervision: startBtSupervision(),
    getBtSupervision: getBtSupervision(),
    updateBtSupervisionInfo: updateBtSupervisionInfo(),
    updateBtSupervisionData: updateBtSupervisionData(),
    stopBtSupervision: stopBtSupervision(),
    deleteBtSupervision: deleteBtSupervision(),
  };

  //Fetch  BtSupervisions
  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;
        if (params) {
          Object.keys(params).forEach((key, index) => {
            if (index === 0) {
              baseSearch += getSupervisionSearchStr(key, params[key]);
            } else {
              baseSearch += `&${getSupervisionSearchStr(key, params[key])}`;
            }
          });
        }
        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });
        return response;
      },
    );
  }

  //Create BtSupervision
  function startBtSupervision() {
    return createAsyncThunk(
      `${name}/startBtSupervision`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  //Get BtSupervision by SupervisionId
  function getBtSupervision() {
    return createAsyncThunk(
      `${name}/getBtSupervision`,
      async ({ btSupervisionId, getAccessTokenSilently }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/${btSupervisionId}/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  //Patch Basic details (BT Supervision info)
  function updateBtSupervisionInfo() {
    return createAsyncThunk(
      `${name}/updateBtSupervisionInfo`,
      async ({ btSupervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${btSupervisionId}/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  //Patch Supervision data (Checkbox data and additional comments)
  function updateBtSupervisionData() {
    return createAsyncThunk(
      `${name}/updateBtSupervisionData`,
      async ({ btSupervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.patch(
          { url: `${baseUrl}/${btSupervisionId}/data/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function stopBtSupervision() {
    return createAsyncThunk(
      `${name}/stopBtSupervision`,
      async ({ btSupervisionId, data, getAccessTokenSilently }: any) =>
        await fetchWrapper.put(
          { url: `${baseUrl}/${btSupervisionId}/stop/`, getAccessTokenSilently },
          data,
        ),
    );
  }

  function deleteBtSupervision() {
    return createAsyncThunk(
      `${name}/deleteBtSupervision`,
      async ({ btSupervisionId, getAccessTokenSilently }: any) =>
        await fetchWrapper.delete({
          url: `${baseUrl}/${btSupervisionId}/`,
          getAccessTokenSilently,
        }),
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<BtSupervisionState>) {
  const {
    getList,
    startBtSupervision,
    getBtSupervision,
    updateBtSupervisionInfo,
    updateBtSupervisionData,
    stopBtSupervision,
    deleteBtSupervision,
  } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: BtSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getList.fulfilled,
      (state: BtSupervisionState, action: PayloadAction<Array<IBtSupervision>>) => {
        state.loading = false;
        state.list = action.payload;
      },
    )
    .addCase(getList.rejected, (state: BtSupervisionState, action: any) => {
      state.list = [];
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // startBtSupervision()
    .addCase(startBtSupervision.pending, (state: BtSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      startBtSupervision.fulfilled,
      (state: BtSupervisionState, action: PayloadAction<IBtSupervision>) => {
        state.loading = false;
        state.current = action.payload;
      },
    )
    .addCase(startBtSupervision.rejected, (state: BtSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getBtSupervision()
    .addCase(getBtSupervision.pending, (state: BtSupervisionState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getBtSupervision.fulfilled,
      (state: BtSupervisionState, action: PayloadAction<any>) => {
        state.current = action.payload;
        state.error = null;
        state.loading = false;
      },
    )
    .addCase(getBtSupervision.rejected, (state: BtSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateBtSupervisionInfo()
    .addCase(updateBtSupervisionInfo.pending, (state: BtSupervisionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateBtSupervisionInfo.fulfilled, (state: BtSupervisionState, action: any) => {
      state.error = null;
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(updateBtSupervisionInfo.rejected, (state: BtSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // updateBtSupervisionData()
    .addCase(updateBtSupervisionData.pending, (state: BtSupervisionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateBtSupervisionData.fulfilled, (state: BtSupervisionState, action: any) => {
      state.error = null;
      state.current = action.payload;
      state.loading = false;
    })
    .addCase(updateBtSupervisionData.rejected, (state: BtSupervisionState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // stopBtSupervision()
    .addCase(stopBtSupervision.pending, (state: BtSupervisionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      stopBtSupervision.fulfilled,
      (_state: BtSupervisionState, _action: PayloadAction<any>) => {
        const toastId = 'btSuperVisionSubmittedToast';
        toast.success('BT Supervision has been submitted', { toastId });
      },
    )
    .addCase(stopBtSupervision.rejected, (state: BtSupervisionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
    })

    // deleteBtSupervision()
    .addCase(deleteBtSupervision.pending, (state: BtSupervisionState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(deleteBtSupervision.fulfilled, (state: BtSupervisionState, _action: any) => {
      state.current = null;
      state.loading = false;
      toast.success('BT Supervision deleted successfully');
    })
    .addCase(deleteBtSupervision.rejected, (state: BtSupervisionState, action: any) => {
      state.error = action.error;
      toast.error(action.error.message);
    });
}

export const btSupervisionActions = { ...slice.actions, ...extraActions };
export const btSupervisionReducer = slice.reducer;
